import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing, Check, Users, ShoppingBag, UserCog } from 'lucide-react';
import { useDueBusinessReminders } from '../../lib/useBusinessReminders';
import { formatBusinessDate, fullName } from '../../lib/businessHelpers';
import { logError } from '../../lib/logger';
import ClientDetailDialog from './ClientDetailDialog';
import ContactDetailDialog from './ContactDetailDialog';
import TeamMemberDetailDialog from './TeamMemberDetailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = { contact: Users, client: ShoppingBag, team: UserCog };
const DETAIL_PATH = { contact: 'contacts', client: 'clients', team: 'team' };

/**
 * Banner "Promemoria di oggi" (stile arancione), riusabile sia nella sezione Business
 * che in Home: mostra ogni promemoria scaduto come riga distinta (una persona con piu'
 * promemoria compare piu' volte). A sinistra del nome compare un'icona esclusiva per il
 * tipo di persona da ricontattare: persona (Contatti Business), borsa della spesa
 * (Clienti) o persona con ingranaggio (Il Mio Team). Al click apre la scheda completa
 * corretta per il tipo. Per "Il Mio Team", se presente, viene mostrato anche l'oggetto
 * testuale del promemoria.
 *
 * `filterType` ("contact" | "client" | "team") limita il banner a una sola categoria:
 * usato nelle pagine Contatti Business/Clienti/Il Mio Team per mostrare solo i promemoria
 * pertinenti a quella sezione. Senza `filterType` (es. in Home) mostra tutte insieme.
 */
const DueRemindersBanner = ({
  enabled = true,
  className = '',
  title = 'Promemoria di oggi',
  refreshSignal,
  filterType,
}) => {
  const { items: allItems, reload, dismiss } = useDueBusinessReminders(enabled);
  const [detailClient, setDetailClient] = useState(null);
  const [detailContact, setDetailContact] = useState(null);
  const [detailTeamMember, setDetailTeamMember] = useState(null);

  useEffect(() => {
    if (refreshSignal !== undefined) reload();
  }, [refreshSignal, reload]);

  const items = filterType ? allItems.filter((it) => it.type === filterType) : allItems;
  const count = items.length;

  const openItem = async (it) => {
    try {
      const res = await axios.get(`${API}/business/${DETAIL_PATH[it.type]}/${it.id}`);
      if (it.type === 'contact') setDetailContact(res.data);
      else if (it.type === 'team') setDetailTeamMember(res.data);
      else setDetailClient(res.data);
    } catch (err) { logError('DueRemindersBanner', err); }
  };

  if (!enabled || count === 0) return null;

  return (
    <>
      <div className={`rounded-2xl border border-orange-200 bg-orange-50 p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <BellRing className="w-4 h-4 text-orange-600" />
          <p className="text-base font-medium text-orange-800 uppercase">{title} ({count})</p>
        </div>
        <ul className="space-y-1.5">
          {items.map((it) => {
            const TypeIcon = TYPE_ICON[it.type] || Users;
            const typeTitle = it.type === 'contact' ? 'Contatto Business' : it.type === 'team' ? 'Membro del Team' : 'Cliente';
            return (
              <li key={`${it.type}-${it.id}-${it.date}`} className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => openItem(it)}
                  className="flex items-center gap-1.5 text-orange-900 hover:underline text-left min-w-0"
                  title={typeTitle}
                >
                  <TypeIcon className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                  <span className="truncate">
                    {fullName(it)} <span className="text-orange-500">· {formatBusinessDate(it.date)}</span>
                    {it.subject ? <span className="text-orange-500"> · {it.subject}</span> : null}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(it)}
                  className="flex items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-900 flex-shrink-0"
                >
                  <Check className="w-3.5 h-3.5" /> Fatto
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <ClientDetailDialog
        open={!!detailClient}
        onOpenChange={(v) => !v && setDetailClient(null)}
        client={detailClient}
      />
      <ContactDetailDialog
        open={!!detailContact}
        onOpenChange={(v) => !v && setDetailContact(null)}
        contact={detailContact}
      />
      <TeamMemberDetailDialog
        open={!!detailTeamMember}
        onOpenChange={(v) => !v && setDetailTeamMember(null)}
        member={detailTeamMember}
      />
    </>
  );
};

export default DueRemindersBanner;
